import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { BASE_URL } from '../config/site';

interface SeoEntry {
  titleKey: string;
  descriptionKey: string;
}

const SEO_MAP: Record<string, SeoEntry> = {
  '/': { titleKey: 'seo.homeTitle', descriptionKey: 'seo.homeDescription' },
  '/workshops': { titleKey: 'seo.workshopsTitle', descriptionKey: 'seo.workshopsDescription' },
  '/about': { titleKey: 'seo.aboutTitle', descriptionKey: 'seo.aboutDescription' },
  '/about-detail': { titleKey: 'seo.aboutTitle', descriptionKey: 'seo.aboutDescription' },
  '/contact': { titleKey: 'seo.contactTitle', descriptionKey: 'seo.contactDescription' },
  '/blog': { titleKey: 'seo.blogTitle', descriptionKey: 'seo.blogDescription' },
  '/blog-detail': { titleKey: 'seo.blogTitle', descriptionKey: 'seo.blogDescription' },
  '/gallery': { titleKey: 'seo.galleryTitle', descriptionKey: 'seo.galleryDescription' },
  '/gallery-detail': { titleKey: 'seo.galleryTitle', descriptionKey: 'seo.galleryDescription' },
  '/privacy-policy': { titleKey: 'seo.privacyTitle', descriptionKey: 'seo.privacyDescription' },
  '/accessibility': { titleKey: 'seo.accessibilityTitle', descriptionKey: 'seo.accessibilityDescription' },
  '/cancellation-policy': { titleKey: 'seo.cancellationTitle', descriptionKey: 'seo.cancellationDescription' },
  '/gift-voucher': { titleKey: 'seo.giftVoucherTitle', descriptionKey: 'seo.giftVoucherDescription' },
};

function getSeoForPath(path: string): SeoEntry {
  if (SEO_MAP[path]) return SEO_MAP[path];

  if (path.startsWith('/workshops/')) {
    return { titleKey: 'seo.workshopDetailTitle', descriptionKey: 'seo.workshopDetailDescription' };
  }
  if (path.startsWith('/blog/')) {
    return { titleKey: 'seo.blogDetailTitle', descriptionKey: 'seo.blogDetailDescription' };
  }

  return { titleKey: 'seo.homeTitle', descriptionKey: 'seo.homeDescription' };
}

const PageTitle: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const seo = getSeoForPath(location.pathname);
  const title = t(seo.titleKey);
  const description = t(seo.descriptionKey);
  const canonicalUrl = `${BASE_URL}${location.pathname === '/' ? '' : location.pathname}`;
  const lang = i18n.language || 'he';

  return (
    <Helmet>
      <html lang={lang} dir={lang === 'he' ? 'rtl' : 'ltr'} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SullaBread" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
};

export default PageTitle;
